import gsap, { Expo, Power3, Power4 } from 'gsap';
import { GetBy } from '../_app/cuchillo/core/Element';
import _Sidemenu from '../_app/cuchillo/layout/Sidemenu';
import { Ease } from '../_app/cuchillo/utils/Ease';
import { Functions } from '../_app/cuchillo/utils/Functions';

const ROTATE = 20;

export default class Sidemenu extends _Sidemenu {

  static content;
  static bg;
  static timeline;
  static texts;
  static lines;
  static _currentLine = null;
  static _currentText = null;

  // Offset d'ocultació per paraula. A part de l'alçada (×3), el rotate fa que un mot
  // ample sobresurti verticalment del seu bounding box en amplada·sin(θ): sense aquest
  // terme, una paraula molt llarga deixava una cantonada visible dins la màscara.
  static _hideY(dom) {
    return dom.offsetHeight * 3 + dom.offsetWidth * Math.sin(ROTATE * Math.PI / 180);
  }

  static init() {
    Sidemenu.directHide();
    Sidemenu.content = GetBy.class("content", this.container)[0];
    Sidemenu.bg = GetBy.class("bg", this.container)[0];
    Sidemenu.timeline = gsap.timeline({ paused: true });
    Sidemenu.texts = [...GetBy.class("__text", this.container)];
    Sidemenu.lines = [...GetBy.class("line", this.container)];
    // Offset d'ocultació per ITEM (no compartit) i tenint en compte la rotació:
    // offsetHeight/Width no es veuen afectats pel transform, així que és segur mesurar-los sempre.
    Sidemenu.texts.map(dom => {
      gsap.set(dom, {
        y: Sidemenu._hideY(dom),
        rotate: ROTATE,
        force3D: true});
    });
  }

  static updateCurrentLink() {
    const currentPath = window.location.pathname;
    const links = [...GetBy.selector('a.nav-item', this.container)];
    links.forEach(a => a.removeAttribute('aria-current'));
    const currentLink = links.find(a => a.getAttribute('href') === currentPath) || null;
    if (currentLink) currentLink.setAttribute('aria-current', 'page');
    Sidemenu._currentLine = currentLink ? GetBy.class('line', currentLink)[0] : null;
    Sidemenu._currentText = currentLink ? GetBy.class('__text', currentLink)[0] : null;
  }

  static show__effect() {
    Sidemenu.updateCurrentLink();
    super.afterShow();
    let delay = 0;
    gsap.killTweensOf(Sidemenu.container);
    gsap.killTweensOf(Sidemenu.bg);
    gsap.killTweensOf(Sidemenu.content);
    // Reset de les línies: alliberem qualsevol clip-path inline residual d'un hide anterior
    // perquè el :hover del CSS torni a controlar-les (i la línia actual es reveli net).
    Sidemenu.lines.forEach(line => { gsap.killTweensOf(line); gsap.set(line, { clearProps: 'clipPath' }); });
    gsap.to(Sidemenu.container, {alpha: 1, duration: 0.3, delay: 0, ease: Power3.easeOut});
    gsap.to(Sidemenu.bg, { scaleY: 1, transformOrigin: "top", duration: .8, delay: .1, ease: Expo.easeOut});
    gsap.to(Sidemenu.content, { scaleY:1, scaleX: 1, duration: .8, delay: .1, ease: Expo.easeOut});

    delay += .0;

    Sidemenu.texts = Functions.arrayRandom(Sidemenu.texts);

    Sidemenu.texts.map(dom => {
      gsap.killTweensOf(dom);
      const isCurrentText = Sidemenu._currentLine && dom === Sidemenu._currentText;
      gsap.to(dom, {
        y: 0,
        duration: 1,
        rotate: 0,
        force3D: true,
        delay: delay,
        ease: Ease.EASE_CUCHILLO_IN_OUT,
        ...(isCurrentText ? {
          onComplete: () => {
            gsap.to(Sidemenu._currentLine, {
              clipPath: 'inset(0 0% 0 0)',
              duration: 0.3,
              ease: 'expo.inOut'
            });
          }
        } : {})
      });
      delay += 0.08;
    });
  }

  static hide__effect(__d = 0) {
    let delay = 0;

    gsap.killTweensOf(Sidemenu.container);
    gsap.killTweensOf(Sidemenu.bg);
    gsap.killTweensOf(Sidemenu.content);

    if (Sidemenu._currentLine) gsap.killTweensOf(Sidemenu._currentLine);

    Sidemenu.texts = Functions.arrayRandom(Sidemenu.texts);
    let currentTextDelay = 0;

    Sidemenu.texts.map(dom => {
      gsap.killTweensOf(dom);
      if (dom === Sidemenu._currentText) currentTextDelay = delay;
      gsap.to(dom, {
        duration: .4,
        y: Sidemenu._hideY(dom),
        rotate: ROTATE,
        force3D: true,
        delay: delay,
        ease: Power4.easeOut});
      delay += 0.01;
    });

    // Totes les línies visibles (la de la pàgina actual + qualsevol revelada per hover) han de
    // retreure's com un hover-out en tancar. Animem el clip-path de cada línia i alliberem la
    // propietat en acabar perquè el :hover del CSS torni a manar quan es reobri el menú.
    Sidemenu.lines.forEach(line => {
      gsap.killTweensOf(line);
      const isCurrent = line === Sidemenu._currentLine;
      gsap.to(line, {
        clipPath: 'inset(0 100% 0 0)',
        duration: 0.2,
        delay: isCurrent ? currentTextDelay : 0,
        ease: 'circ.in',
        onComplete: () => gsap.set(line, { clearProps: 'clipPath' })
      });
    });

    gsap.to(Sidemenu.container, {alpha: 0, duration: 0.2, delay: .1, ease: Power3.easeIn,
      onComplete: () => {
        super.afterHide();
      }});
    gsap.to(Sidemenu.bg, { scaleY: 0, transformOrigin: "bottom", duration: .8, delay: .1, ease: Power4.easeOut});
    gsap.to(Sidemenu.content, { scaleY:1.1, scaleX: 1.1, duration: .8, delay: .1, ease: Power4.easeOut});
  }

  static resize() {
    super.resize();
    // Menú tancat: re-apliquem l'offset d'ocultació per item amb la nova alçada,
    // perquè no quedin parcialment visibles si el layout ha canviat.
    if (!Sidemenu.isOpen) {
      Sidemenu.texts.forEach(dom => gsap.set(dom, { y: Sidemenu._hideY(dom) }));
    }
  }
}
